<template>
  <bootstrap>
    <div class="reservation-display">
      <button
        :class="{
          details: true,
          'curbside-icon': fulfillmentTypeDisplay === 'curbside',
          'delivery-icon': fulfillmentTypeDisplay === 'delivery',
          'details--show-expiration': showExpiration
        }"
        v-if="fulfillmentType && !hideDisplay"
        @click="showModal('change-timeslot-modal')"
        ref="reservationButton"
        :title="
          `${locationName} - ${
            isActiveReservation && dateTimeRange
              ? dateTimeRange
              : 'Reserve a time slot'
          }`
        "
      >
        <span>
          <span class="fulfillment-type">{{ fulfillmentTypeDisplay }} </span>
          <span>
            at
            <strong>{{ locationName }} </strong>
          </span>
        </span>
        <span v-if="isActiveReservation && dateTimeRange">
          on
          <strong>{{ dateTimeRange }} </strong>
        </span>
        <span v-else>
          -
          <strong>Reserve a time slot</strong>
        </span>
      </button>

      <span
        v-show="showExpiration"
        v-if="isActiveReservation && expirationTime"
        class="expires expires--active"
        data-message-type="alert"
        :class="{ 'expires--hide-for-mobile': hideActiveExpirationForMobile }"
      >
        Time slot will be held for you until
        <em>{{ expirationTime }}</em>
      </span>
      <span
        v-show="showExpiration"
        v-if="!isActiveReservation && expirationTime"
        class="expires expires--passed"
      >
        <em data-message-type="alert">Your time slot has expired!</em>
      </span>
    </div>

    <!--
            TODO: Move all these modals (below) to somewhere they can be included once
            into every page (maybe pageEnd.jsp, or a single component to be added there?)
            This will let us add this Display component to both the desktop & mobile headers
            without also adding these modals multiple times.
            This will also allow us to use these modals to a page
            even when this Display isn't needed (like on the "online" search & PDP's)
    -->

  </bootstrap>
</template>

<script>
import moment from "moment";
import { reservation } from "../mixins/reservation";

export default {
  name: "ReservationDisplay",

  mixins: [reservation],

  props: {
    /** should we show the expiration message? */
    showExpiration: {
      type: Boolean,
      default: false
    },

    /** should we hide the main display? */
    hideDisplay: {
      type: Boolean,
      default: false
    },

    /** for when we only want to hide an active (non-expired) expiration time, but only for mobile */
    hideActiveExpirationForMobile: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    isOnCartPage() {
      return _.get(this, "$route.name", false) === "Cart";
    },

    /** @return {string} location nickname */
    locationName() {
      const { location = {} } = this.reservation;
      return location.nickname || "";
    },

    /** @return {string} fulfillment type */
    fulfillmentTypeDisplay() {
      return this.fulfillmentTypeMap[this.fulfillmentType];
    },

    /** @return {string} date-time range for display */
    dateTimeRange() {
      const { startTime, endTime } = this.reservation;

      return startTime && endTime
        ? moment.parseZone(startTime).format("MMM D, h:mmA") +
            "-" +
            moment.parseZone(endTime).format("h:mmA")
        : "";
    },

    /** @return {string} expiration time for display */
    expirationTime() {
      const { expiration = {} } = this.reservation;
      return expiration.at
        ? moment(expiration.at).format("h:mm A")
        : "";
    },
    cartLoaded() {
      // #pilotLaunchUI
      return this.$store.state.cart.loaded;
    }
  },
  methods: {
    cleanUrl() {
      // Remove queryStrings from URL
      var uri = window.location.toString();
      if (uri.indexOf("?") > 0) {
        var clean_uri = uri.substring(0, uri.indexOf("?"));
        window.history.replaceState({}, document.title, clean_uri);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../assets/lib";

.reservation-display {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.fulfillment-type {
  color: #717172;
  text-transform: capitalize;
}

strong {
  font-weight: $font-weight-bold;
  text-transform: capitalize;
  white-space: nowrap;
}

em {
  color: $color-heb-primary;
}

.details,
.expires {
  padding: 11px 0px;
  font-size: 14px;
  line-height: 20px;
}

.details {
  position: relative;
  display: inline-block;
  width: 100%;
  padding-left: 16px;
  padding-right: 40px;
  text-align: left;
  border: 1px solid $color-border-standard-gray;
  border-radius: $border-radius-standard;
  background-color: #fff;
  margin-bottom: 0; // override foundation
  font-family: "Open Sans", sans-serif; // override foundation
  color: $color-text-standard; // override foundation
  text-overflow: ellipsis;
  overflow-x: hidden;

  & > span {
    white-space: nowrap;
    display: inline-block;
    @media (min-width: $breakpoint-tablet) {
      display: inline;
    }
  }

  @media (min-width: $breakpoint-desktop) {
    width: 100%;
  }
  @media (min-width: $breakpoint-tablet) {
    white-space: nowrap;
    max-width: 45vw;
  }

  // .details--show-expiration
  &--show-expiration {
    @media (min-width: $breakpoint-tablet) {
      width: auto;
    }
  }

  // down arrow CTA
  &:after {
    position: absolute;
    top: 13px;
    right: 15px;
    font: {
      family: FontAwesome;
      size: 18px;
    }
    color: $color-text-gray;
    content: "\f107";
  }
}

.expires {
  display: inline-block;
  font-weight: $font-weight-boldish;

  @media (min-width: $breakpoint-tablet) {
    margin-left: 16px;
  }

  &--hide-for-mobile {
    display: none;

    @media (min-width: $breakpoint-tablet) {
      display: block;
    }
  }
}
</style>

<style lang="scss">
// This component is standalone, and therefore needs curbside styles
@import "../../styles.scss";

@media screen and (min-width: 320px) {
  .modal-footer .btn-primary {
    white-space: nowrap;
  }
}
</style>

<docs>
    This component can display the following types: `pickup` (displayed as "curbside") || `delivery`

    This component is responsive:
    - "expires at" section wraps to a new line on smaller screens
    - "strong" blocks (i.e., location, time) are set to not break/wrap

    The default view for a "pickup" or "curbside" timeslot display

    ```jsx
        const timeslot = {
            startTime: '2018-09-10 11:00:00-05:00',
            endTime: '2018-09-10 12:00:00-05:00',
            expiry: '2018-09-07 17:04:02-05:00',
            fulfillmentType: 'pickup'
        };

        <timeslot-display></timeslot-display>
    ```

    The default view for a "delivery" timeslot display

    ```jsx
        const timeslot = {
            startTime: '2018-09-10 11:00:00-05:00',
            endTime: '2018-09-10 12:00:00-05:00',
            expiry: '2018-09-07 17:04:02-05:00',
            fulfillmentType: 'pickup'
        };

        <timeslot-display v-bind:showExpiration="true"></timeslot-display>
    ```

    An example without "expiration"

    ```jsx
        const timeslot = {
            startTime: '2018-09-10 11:00:00-05:00',
            endTime: '2018-09-10 12:00:00-05:00',
            expiry: '2018-09-07 17:04:02-05:00',
            fulfillmentType: 'pickup'
        };

        <timeslot-display></timeslot-display>
    ```

    ### Specifications:

    1. display the correct icon
    1. display the correct fulfillment type name
    1. display the location (when there is one)
    1. on click, open a modal
    1. modal should have 2 tabs, with the correct tab selected
    1. modal should display the pickup/delivery address
    1. modal should display the timeslot picker
    1. modal should show the correct header messaging
    1. modal should be responsive

    #### When "my timeslot" is set:

    1. display the date/time

    #### When "my timeslot" is NOT set:

    1. display a message asking the user to reserve a timeslot
</docs>
